import React, { useEffect, useState } from 'react';
import IconItem from '../product-masthead/icon-item';
import ImageSwiper from './image-swiper';
import ToggleSwitch from '../toggle-switch';
import Autocomplete from './autocomplete';
import ColorSwiper from './color-swiper';
import { getStoredGeolocationData } from '../../helpers/geolocation';
import { makeSuperScript } from '../../helpers/helpers';
import {
  ExtendedWindowType,
  MediaType,
  ProductMastheadResponseType,
  VariantOptionType
} from '../../helpers/global-types';

type ShingleDetailMastheadProps = {
  endpointColors: string;
  bazaarReview: string;
  toggleOptions: string;
  formAddress: string;
  nextLabel: string;
  prevLabel: string;
  defaultImage: string;
  noResultsError: string;
  hideToggle?: boolean;
};

const ShingleDetailMasthead = ({
  endpointColors,
  bazaarReview,
  toggleOptions,
  formAddress,
  nextLabel,
  prevLabel,
  defaultImage,
  noResultsError,
  hideToggle
}: ShingleDetailMastheadProps) => {
  const [data, setData] = useState<ProductMastheadResponseType>();
  const [error, setError] = useState(false);
  const [displayImages, setDisplayImages] = useState<MediaType[]>([]);
  const [displayColors, setDisplayColors] = useState<MediaType[]>([]);
  const [yourAreaColors, setYourAreaColors] = useState<string[]>([]);
  const [availableColors, setAvailableColors] = useState<string[]>([]);
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [defaultColor, setDefaultColor] = useState<string>('');
  const [defaultSetup, setDefaultSetup] = useState(false);
  const [addressError, setAddressError] = useState(false);
  const toggleOptionsObj = JSON.parse(toggleOptions);
  const addressInput = JSON.parse(formAddress);
  const [personalizeButtons, setPersonalizeButtons] = useState('');
  const [address, setAddress] = useState<{
    address: string;
    zip: string;
    country: string;
  }>({
    address: '',
    zip: '',
    country: ''
  });
  const [selectedAttributes, setSelectedAttributes] = useState<
    VariantOptionType[]
  >([]);
  const [selectedArea, setSelectedArea] = useState(0);
  const bazarRef = React.createRef<HTMLDivElement>();

  const handleDefaultSelectedAttributes = () => {
    let _finalSelectedAttributes: VariantOptionType[] = [];
    let _finalSelectedColor = '';

    if (data?.defaultVariation !== '') {
      const selectedVariation = data?.pid_attribute_option_index.find(
        item => item.magento_product_id === data?.defaultVariation
      );

      if (selectedVariation) {
        const selectedOptions: VariantOptionType[] = [];
        data?.variants.forEach(variant => {
          const correspondingVariant =
            selectedVariation.attribute_option_items.find(
              attributeOption => attributeOption.attribute_id === variant.id
            );
          if (correspondingVariant) {
            const selectedOption = variant.options.find(
              opt => opt.id === correspondingVariant.option_id
            );
            if (selectedOption) {
              selectedOptions.push(selectedOption);
            }
          }
        });

        if (selectedOptions.length === 0) {
          data?.variants.forEach(variant => {
            selectedOptions.push(variant.options[0]);
          });
        }

        const selectedMediaVariant = data?.mediaVariants.find(
          variant =>
            variant.magento_product_id === selectedOptions[0].products[0]
        );

        const productDetails = selectedMediaVariant?.attribute_groups.find(
          group => group.attribute_group_name === 'Product Details'
        );

        const colorId = productDetails?.attributes.find(
          attribute => attribute.attribute_code === 'colorid'
        )?.attribute_value;

        if (data?.colors.includes(colorId as string)) {
          _finalSelectedAttributes = selectedOptions;
        } else {
          data?.variants.forEach(variant => {
            selectedOptions.push(variant.options[0]);
          });
        }
      } else {
        const selectedOptions: VariantOptionType[] = [];
        data?.variants.forEach(variant => {
          selectedOptions.push(variant.options[0]);
        });
        _finalSelectedAttributes = selectedOptions;
      }

      const selectedVariant = data?.mediaVariants.find(
        variant => variant.magento_product_id === data?.defaultVariation
      );
      const productDetails = selectedVariant?.attribute_groups.find(
        group => group.attribute_group_name === 'Product Details'
      );
      const colorId = productDetails?.attributes.find(
        attribute => attribute.attribute_code === 'colorid'
      )?.attribute_value;

      if (colorId) {
        _finalSelectedColor = colorId;
        setDefaultColor(colorId as string);
      } else if (data?.colors) {
        _finalSelectedColor = data?.colors[0];
      }
    }

    const _selectedColor = sessionStorage.getItem('selectedColor');
    sessionStorage.removeItem('selectedColor');

    if (_selectedColor) {
      const selectedMediaVariant = data?.mediaVariants.find(variant => {
        const productDetails = variant.attribute_groups.find(
          group => group.attribute_group_name === 'Product Details'
        );
        if (
          productDetails?.attributes.find(
            attribute =>
              attribute.attribute_code === 'colorid' &&
              attribute.attribute_value === _selectedColor
          )
        )
          return variant;
        return false;
      });

      const _selectedAttribute = data?.variants.map(variant =>
        variant?.options.find(option =>
          option.products.includes(
            selectedMediaVariant?.magento_product_id as string
          )
        )
      );
      if (_selectedAttribute) {
        _finalSelectedAttributes = _selectedAttribute as VariantOptionType[];
        _finalSelectedColor = _selectedColor;
      }
    }

    setSelectedAttributes(_finalSelectedAttributes as VariantOptionType[]);
    setSelectedColor(_finalSelectedColor);
    setDefaultSetup(true);
  };

  const handleDisplayMedia = () => {
    const mapping = data?.pid_attribute_option_index;
    const id = mapping?.find(item => {
      return data?.variants.every((variant, index) => {
        const selProduct = item.attribute_option_items.find(
          option =>
            option.attribute_id === variant.id &&
            selectedAttributes[index].id === option.option_id
        );
        return selProduct;
      });
    });

    const mediaImage = data?.mediaVariants.find(mediaVariant => {
      if (mediaVariant.magento_product_id === id?.magento_product_id) {
        if (mediaVariant.media.length > 0) {
          return mediaVariant;
        }
      }
      return false;
    });
    if (!mediaImage && data?.productDescription?.defaultImages) {
      if (data?.productDescription?.defaultImages.length > 0) {
        setDisplayImages(data?.productDescription.defaultImages);
      } else {
        setDisplayImages(JSON.parse(defaultImage));
      }
    } else if (!mediaImage) {
      setDisplayImages(JSON.parse(defaultImage));
    } else {
      setDisplayImages(mediaImage.media);
    }
  };

  const handleDisplayColors = () => {
    const _displayColors = data?.productDescription.colorImages.filter(
      colorImage => availableColors?.includes(colorImage.color_id as string)
    );

    getVariantIsAvailable();

    if (_displayColors && _displayColors.length > 0) {
      setDisplayColors(_displayColors as MediaType[]);
    } else {
      setDisplayColors([]);
    }
  };

  const handleSelected = (value: string) => {
    (window as ExtendedWindowType).dataLayer.push({
      event: 'product_click',
      event_category: 'product',
      event_action: 'variant selection',
      event_label: 'shingle color',
      event_attributes: {
        product_name: data?.productDescription.heading,
        product_category: 'Shingle',
        average_rating: (
          document.querySelector(
            '.bv_avgRating_component_container'
          ) as HTMLElement
        )?.innerText
      }
    });
    setSelectedColor(value);
    const selectedMediaVariant = data?.mediaVariants.find(variant => {
      const productDetails = variant.attribute_groups.find(
        group => group.attribute_group_name === 'Product Details'
      );
      if (
        productDetails?.attributes.find(
          attribute =>
            attribute.attribute_code === 'colorid' &&
            attribute.attribute_value === value
        )
      )
        return variant;

      return false;
    });
    const _selectedAttribute = data?.variants.map(variant =>
      variant?.options.find(option =>
        option.products.includes(
          selectedMediaVariant?.magento_product_id as string
        )
      )
    );
    if (_selectedAttribute)
      setSelectedAttributes(_selectedAttribute as VariantOptionType[]);
  };

  const handleSwitchChange = (value: string) => {
    const _selectedArea = toggleOptionsObj.findIndex(
      (option: { value: string }) => option.value === value
    );
    setSelectedArea(_selectedArea);

    if (_selectedArea === 0) {
      if (data?.colors && data?.colors.length > 0) {
        setAvailableColors(data?.colors);
      } else {
        setAvailableColors([]);
      }
    } else {
      setAvailableColors(yourAreaColors);
    }
  };

  const getVariantIsAvailable = () => {
    const selectedVariant = data?.mediaVariants.find(
      variant =>
        variant.magento_product_id === selectedAttributes[0].products[0]
    );
    const productDetails = selectedVariant?.attribute_groups.find(
      group => group.attribute_group_name === 'Product Details'
    );
    const colorId = productDetails?.attributes.find(
      attribute => attribute.attribute_code === 'colorid'
    )?.attribute_value;

    if (!availableColors.includes(colorId as string)) {
      data?.mediaVariants.find(variant => {
        const details = variant.attribute_groups.find(
          group => group.attribute_group_name === 'Product Details'
        );
        const detailColor = details?.attributes.find(
          attribute => attribute.attribute_code === 'colorid'
        )?.attribute_value;

        if (detailColor && availableColors[0] === detailColor) {
          const newOptionType = data?.variants[0].options.find(option =>
            option?.products.includes(variant.magento_product_id)
          );
          if (newOptionType) {
            setSelectedAttributes([newOptionType]);
            setSelectedColor(detailColor);
          }
        }
      });
    }
  };

  useEffect(() => {
    if (data) {
      const savedData = getStoredGeolocationData();

      if (savedData && !hideToggle) {
        setSelectedArea(1);

        setAddress({
          address: savedData.address as string,
          zip: savedData.zip,
          country: savedData.country
        });
      }

      if (data.colors) {
        setAvailableColors(data?.colors);
      }
      handleDefaultSelectedAttributes();
    }
  }, [data]);

  useEffect(() => {
    handleDisplayMedia();
  }, [selectedAttributes]);

  useEffect(() => {
    handleDisplayColors();
  }, [availableColors]);

  useEffect(() => {
    const productId = data?.productDescription.product;

    if (address.zip && productId) {
      let _country = address.country;
      if (_country.toUpperCase() === 'US') {
        _country = 'USA';
      } else if (_country.toUpperCase() === 'CA') {
        _country = 'CAN';
      }

      fetch(
        endpointColors
          .replace('{zipCode}', address.zip)
          .replace(' ', '')
          .replace('{prod}', productId)
          .replace('{countryCode}', _country),
        {
          method: 'GET'
        }
      )
        .then(response => response.json())
        .then(data => {
          if (data?.availableColors) {
            setAvailableColors(data?.availableColors);
            setYourAreaColors(data?.availableColors);
          } else {
            setAvailableColors([]);
            setYourAreaColors([]);
          }
        })
        .catch(error => {
          console.error('Error:', error);
        });
    }
  }, [address, data]);

  if (error) {
    return <p>Error on loading</p>;
  }

  const handleBazarClick = () => {
    const reviewsSection = document.querySelector(
      '[data-module="review-embed"]'
    ) as HTMLElement;
    const sectionName = reviewsSection.dataset?.section;
    const sectionId = reviewsSection.id;
    const navigationLinks = document.querySelectorAll(
      '.navigation-jumplinks__link'
    );
    const dropdown = document.querySelector(
      '.navigation-jumplinks .field__select'
    ) as HTMLSelectElement;

    window.dispatchEvent(new Event('pause-jumplinks'));

    if (navigationLinks) {
      navigationLinks.forEach(link => {
        if (link.ariaLabel !== sectionName) {
          link.classList.remove('active');
        } else {
          link.classList.add('active');
        }
      });
    }

    if (dropdown) {
      setTimeout(() => {
        dropdown.value = sectionId;
      }, 500);
    }
  };

  useEffect(() => {
    if (bazarRef.current) {
      const bazarContainer = bazarRef.current;
      let maxTries = 10;

      const interval = setInterval(() => {
        maxTries--;
        const button = bazarContainer.querySelector('.bv_ratings_summary');

        if (button) {
          clearInterval(interval);
          button.addEventListener('click', handleBazarClick.bind(this));

          window.addEventListener('click', e => {
            const target = e.target as HTMLElement;

            if (target.closest('.bv_modal_inner_content')) {
              handleBazarClick();
            }
          });
        }

        if (maxTries === 0) {
          clearInterval(interval);
        }
      }, 1000);
    }
  }, [bazarRef, data]);

  useEffect(() => {
    let observer: MutationObserver;

    const targetNode = document.querySelector('.personalize-buttons');

    if (targetNode) {
      const targetInnerHtml = targetNode.innerHTML;

      setPersonalizeButtons(targetInnerHtml);

      const config = { attributes: true, childList: true, subtree: true };

      const callback = (mutationList: MutationRecord[]) => {
        for (const mutation of mutationList) {
          if (mutation.type === 'childList') {
            setPersonalizeButtons(targetNode.innerHTML);
          }
        }
      };

      observer = new MutationObserver(callback);
      observer.observe(targetNode, config);
    }

    const newData = (window as ExtendedWindowType).mastheadData;

    if (newData) {
      setData((window as ExtendedWindowType).mastheadData);
    } else {
      setError(true);
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, []);

  return (
    <div className="shingle-detail-masthead__wrapper shingle-detail-masthead__wrapper--full">
      {defaultSetup && (
        <div className="shingle-detail-masthead__content">
          <div className="shingle-detail-masthead__top-content">
            {data?.productDescription.icon && (
              <IconItem
                src={data?.productDescription.icon.src}
                label={data?.productDescription.icon.label}
              />
            )}
            <div className="shingle-detail-masthead__info">
              <h1
                className="shingle-detail-masthead__heading rtf--inline"
                dangerouslySetInnerHTML={{
                  __html: makeSuperScript(
                    data?.productDescription?.heading as string
                  )
                }}
              />
              <div
                className="shingle-detail-masthead__description"
                dangerouslySetInnerHTML={{
                  __html: makeSuperScript(
                    data?.productDescription?.description as string
                  )
                }}
              />
            </div>
            <div className="shingle-detail-masthead__cta-wrapper">
              {data?.productDescription?.product && (
                <div
                  ref={bazarRef}
                  className="shingle-detail-masthead__bazaar-review"
                  dangerouslySetInnerHTML={{
                    __html: bazaarReview.replace(
                      '{prod}',
                      data?.productDescription?.product
                    )
                  }}
                ></div>
              )}
              {personalizeButtons && (
                <div
                  className="shingle-detail-masthead__button-row"
                  dangerouslySetInnerHTML={{ __html: personalizeButtons }}
                ></div>
              )}
            </div>
          </div>
          <div className="shingle-detail-masthead__swiper">
            <ImageSwiper
              fullScreen={hideToggle}
              images={displayImages}
              nextLabel={nextLabel}
              prevLabel={prevLabel}
              productHeading={
                data?.productDescription?.heading?.toString() || ''
              }
            />
          </div>

          {!hideToggle && (
            <div className="row">
              <div
                className="shingle-detail-masthead__toggle"
                style={{ blockSize: addressInput.tooltip ? '51px' : '49px' }}
              >
                <ToggleSwitch
                  onValueChange={handleSwitchChange}
                  values={toggleOptionsObj}
                  defaultValue={toggleOptionsObj[selectedArea].value}
                />
              </div>
              {selectedArea === 1 && (
                <div className="shingle-detail-masthead__autocomplete">
                  <Autocomplete
                    label={addressInput.label}
                    name={addressInput.name}
                    placeholder={addressInput.placeholder}
                    tooltip={addressInput.tooltip}
                    type={addressInput.type}
                    error={addressError}
                    setError={setAddressError}
                    errorLabel={addressInput.error}
                    clearLabel={addressInput.clear || 'Clear'}
                    setValue={setAddress}
                    value={address.address}
                    countries={['us', 'ca']}
                  />
                </div>
              )}
            </div>
          )}

          {displayColors.length > 0 ? (
            <ColorSwiper
              fullScreen={hideToggle}
              images={displayColors}
              defaultColor={defaultColor}
              handleSelected={handleSelected}
              nextLabel={nextLabel}
              prevLabel={prevLabel}
              selected={selectedColor}
            />
          ) : (
            <div>
              <p className="color-swiper__error-message">
                {noResultsError?.replace('{zipCode}', address.zip)}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
export default ShingleDetailMasthead;
